import { lazy } from 'react';
import { isMobile } from 'helpers';

const CHUNK_RETRY_PARAM = 'cr';
const RETRY_WINDOW_MS = 30_000;
const IMPORT_RETRY_COUNT = 2;
const IMPORT_RETRY_DELAY_MS = 500;

const isChunkError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const message = err.message || '';
  return name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/i.test(message);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const retryImport = async (factory, attemptsLeft = IMPORT_RETRY_COUNT) => {
  try {
    return await factory();
  } catch (err) {
    if (attemptsLeft > 0 && isChunkError(err)) {
      await wait(IMPORT_RETRY_DELAY_MS);
      return retryImport(factory, attemptsLeft - 1);
    }
    throw err;
  }
};

const hasRecentReloadAttempt = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const ts = Number(params.get(CHUNK_RETRY_PARAM));
    return Number.isFinite(ts) && ts > 0 && Date.now() - ts < RETRY_WINDOW_MS;
  } catch {
    return false;
  }
};

const reloadWithRetryParam = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CHUNK_RETRY_PARAM, String(Date.now()));
    window.location.replace(url.toString());
    return true;
  } catch {
    return false;
  }
};

const lazyWithRetry = (factory) => lazy(() => retryImport(factory).catch((err) => {
  if (isChunkError(err) && !isMobile && !hasRecentReloadAttempt() && reloadWithRetryParam()) {
    // Page is navigating away; hold Suspense until reload destroys this context.
    return new Promise(() => {});
  }
  throw err;
}));

export default lazyWithRetry;
